<template>
  <article class="self-service-card wide-card">
    <div class="participant-header">
      <div class="participant-header__nav">
        <button type="button" class="ghost-action compact" @click="front.backToConferenceList">← 返回会议列表</button>
        <button type="button" class="ghost-action compact" @click="front.backToConferencePayment">报名缴费</button>
      </div>
      <h3>填写参会及报告信息</h3>
      <p v-if="currentConferenceTitle" class="participant-current-conf">
        <strong>{{ currentConferenceTitle }}</strong>
        <span v-if="currentRegistration">· {{ front.registrationStatusLabel(currentRegistration) }}</span>
      </p>
    </div>
    <p v-if="front.canFillAttendeeInfo" class="service-hint">
      <template v-if="front.canFillFieldTrip">
        发票终审已通过，会议费缴纳完成。请在截止日前填写参会信息并上传摘要；野外报名现已开放。
      </template>
      <template v-else>
        凭证已通过：可先填写参会基础信息、摘要与住宿。野外报名需待发票终审通过后开放。
      </template>
    </p>
    <p
      v-if="front.selfServiceStatus"
      ref="statusNotice"
      class="service-notice"
      :class="{ warning: isStatusWarning, success: isStatusSuccess }"
    >
      {{ front.selfServiceStatus }}
    </p>
    <div v-if="!currentRegistration" class="service-notice warning" role="alert">
      <strong>未找到当前会议报名</strong>
      <span>请返回会议列表，从对应会议进入「填写参会信息」。</span>
    </div>
    <div v-else class="participant-form">
      <p v-if="front.participantLockMessage" class="service-notice warning">{{ front.participantLockMessage }}</p>
      <label>
        姓名
        <input v-model.trim="front.participantForm.participantName" type="text" :disabled="!front.canFillAttendeeInfo" placeholder="参会人姓名" />
      </label>
      <label>
        性别
        <select v-model="front.participantForm.participantSex" :disabled="!front.canFillAttendeeInfo">
          <option value="MALE">男</option>
          <option value="FEMALE">女</option>
        </select>
      </label>
      <label>
        单位
        <input v-model.trim="front.participantForm.participantUnit" type="text" :disabled="!front.canFillAttendeeInfo" placeholder="工作或学习单位" />
      </label>
      <label>
        身份
        <input v-model.trim="front.participantForm.participantRole" type="text" :disabled="!front.canFillAttendeeInfo" placeholder="教师、学生或嘉宾" />
      </label>
      <label>
        意向专场
        <select
          v-if="front.selectedSessionOptions.length"
          v-model="front.participantForm.sessionName"
          :disabled="!front.canFillAttendeeInfo"
        >
          <option value="">请选择意向分会场/专场</option>
          <option
            v-for="session in front.selectedSessionOptions"
            :key="session.id"
            :value="session.name"
          >
            {{ session.name }}
          </option>
        </select>
        <input
          v-else
          v-model.trim="front.participantForm.sessionName"
          type="text"
          :disabled="!front.canFillAttendeeInfo"
          placeholder="请填写意向分会场或专场"
        />
        <small v-if="front.selectedSessionOptions.length" class="service-hint">选项来自该会议后台配置的分会场</small>
        <small v-else class="service-hint">该会议尚未配置分会场，可先手填；管理员可在会议编辑中添加</small>
      </label>
      <label>
        报告类型
        <select v-model="front.participantForm.reportType" :disabled="!front.canFillAttendeeInfo">
          <option value="ORAL">口头报告</option>
          <option value="POSTER">展板报告</option>
          <option value="NONE">仅参会</option>
        </select>
      </label>
      <label>
        报告题目
        <input
          v-model="front.participantForm.reportTitle"
          type="text"
          :disabled="!front.canFillAttendeeInfo || front.participantForm.reportType === 'NONE'"
          :placeholder="front.participantForm.reportType === 'NONE' ? '仅参会无需填写' : '请填写报告题目'"
        />
      </label>

      <div v-if="needsAbstract" class="abstract-block">
        <strong>上传论文摘要（Word）</strong>
        <p class="service-hint">仅支持 .doc / .docx，不超过 10MB；多次上传只保留最新版。</p>
        <div v-if="currentAbstractName" class="abstract-file">
          <div class="abstract-file__meta">
            <span>已上传：</span>
            <a
              v-if="currentAbstractUrl"
              class="abstract-file__link"
              :href="currentAbstractUrl"
              target="_blank"
              rel="noopener"
            >{{ currentAbstractName }}</a>
            <span v-else>{{ currentAbstractName }}</span>
          </div>
          <div class="abstract-file__actions">
            <button
              v-if="currentAbstractUrl"
              type="button"
              class="ghost-action compact"
              @click="openAbstract"
            >
              查看
            </button>
            <button
              type="button"
              class="ghost-action compact"
              :disabled="!front.canFillAttendeeInfo || abstractUploading"
              @click="triggerAbstractPick"
            >
              重新上传
            </button>
            <button
              type="button"
              class="ghost-action compact"
              :disabled="!front.canFillAttendeeInfo || abstractUploading || abstractDeleting"
              @click="onDeleteAbstract"
            >
              {{ abstractDeleting ? '删除中…' : '删除' }}
            </button>
          </div>
        </div>
        <button
          v-else
          type="button"
          class="primary-action compact"
          :disabled="!front.canFillAttendeeInfo || abstractUploading"
          @click="triggerAbstractPick"
        >
          {{ abstractUploading ? '上传中…' : '选择摘要文件' }}
        </button>
        <input
          ref="abstractInput"
          class="abstract-input-hidden"
          type="file"
          accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          :disabled="!front.canFillAttendeeInfo || abstractUploading"
          @change="onAbstractChange"
        />
      </div>
      <p v-else class="service-hint">仅参会无需上传摘要。如需提交摘要，请将报告类型改为口头报告或展板报告。</p>

      <label>
        住宿
        <select v-model="front.participantForm.accommodationType" :disabled="!front.canFillAttendeeInfo">
          <option
            v-for="item in front.availableAccommodationOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
      </label>
      <section v-if="front.activeAccommodationConfig" class="accommodation-config" aria-label="本场会议住宿配置">
        <strong>本场会议住宿配置</strong>
        <p>{{ front.activeAccommodationConfig }}</p>
        <small>请结合以上房间、床位或酒店说明选择对应房型；最终住宿安排以会议方通知为准。</small>
      </section>
      <label v-if="!front.hasConferenceFieldTripRoutes">
        野外阶段
        <select v-model="front.participantForm.fieldTripStage" :disabled="!front.canFillFieldTrip" @change="onFieldTripStageChange">
          <option value="">不参加野外</option>
          <option value="pre">会前</option>
          <option value="during">会中</option>
          <option value="post">会后</option>
        </select>
      </label>
      <div v-if="front.hasConferenceFieldTripRoutes" class="field-trip-block">
        <div class="field-trip-block__head">
          <strong>野外路线</strong>
          <span v-if="front.availableFieldTripRoutes.length" class="field-trip-count">
            已选 {{ selectedRouteIds.length }} / {{ front.availableFieldTripRoutes.length }}
          </span>
        </div>
        <p class="field-trip-tip">
          {{ front.fieldTripOnlyPost ? '本场仅设置会后野外路线，请单选一条。' : '可按会前、会中、会后跨阶段多选路线。' }}
        </p>
        <div v-if="front.availableFieldTripRoutes.length">
          <section v-for="group in front.fieldTripRouteGroups" :key="group.phase" class="field-trip-group">
            <h4 v-if="!front.fieldTripOnlyPost">{{ group.label }}</h4>
            <div
              class="field-trip-grid"
              role="group"
              :aria-label="front.fieldTripOnlyPost ? '会后野外路线单选' : `${group.label}路线多选`"
            >
              <button
                v-for="route in group.routes"
                :key="route.id"
                type="button"
                class="field-trip-chip"
                :class="{ active: isRouteSelected(route.id), disabled: !front.canFillFieldTrip, radio: front.fieldTripOnlyPost }"
                :disabled="!front.canFillFieldTrip"
                :aria-pressed="isRouteSelected(route.id) ? 'true' : 'false'"
                @click="toggleRoute(route.id)"
              >
                <span class="field-trip-chip__check" aria-hidden="true" />
                <span class="field-trip-chip__body">
                  <span class="field-trip-chip__name">{{ route.name }}</span>
                  <span class="field-trip-chip__fee">路线费用：{{ formatRouteFee(route.fee) }}</span>
                  <span
                    v-if="route.genderRestriction === 'male' || route.genderRestriction === 'female'"
                    class="field-trip-chip__tag"
                  >
                    {{ route.genderRestriction === 'male' ? '仅男性' : '仅女性' }}
                  </span>
                </span>
              </button>
            </div>
          </section>
        </div>
        <p v-else class="service-hint">当前没有符合性别限制的可选路线。</p>
      </div>
      <label v-else>
        野外路线
        <input
          v-model="front.participantForm.fieldTripRoute"
          type="text"
          :disabled="!front.canFillFieldTrip"
          placeholder="该会议尚未配置野外路线"
        />
      </label>
      <p class="service-hint">路线费用为会议方配置的科考费用条目，当前不并入已锁定的会议费；野外报名将绑定基本信息中的性别。</p>
      <p
        v-if="front.selfServiceStatus"
        class="service-notice save-feedback"
        :class="{ warning: isStatusWarning, success: isStatusSuccess }"
      >
        {{ front.selfServiceStatus }}
      </p>
      <button
        type="button"
        class="primary-action compact"
        :disabled="!front.canFillAttendeeInfo || abstractUploading || saving"
        @click="onSave"
      >
        {{ saving ? '保存中…' : '保存参会资料' }}
      </button>
    </div>
  </article>
</template>

<script>
export default {
  name: 'ParticipantInfoCard',
  inject: ['front'],
  data() {
    return {
      abstractUploading: false,
      abstractDeleting: false,
      saving: false,
      selectedRouteIds: []
    }
  },
  watch: {
    'front.participantForm.registrationId'() {
      this.$nextTick(() => this.loadFieldTripSelection())
    },
    'front.conferenceFieldTripRoutes'() {
      this.$nextTick(() => this.loadFieldTripSelection())
    },
    'front.participantForm.participantSex'() {
      this.$nextTick(() => {
        this.pruneInvalidAccommodation()
        this.pruneInvalidRoutes()
      })
    }
  },
  mounted() {
    this.loadFieldTripSelection()
  },
  computed: {
    currentRegistration() {
      return this.front.activeParticipantRegistration
    },
    currentConferenceTitle() {
      const registration = this.currentRegistration
      if (!registration) {
        return (this.front.selectedConference && this.front.selectedConference.conferenceTitle) || ''
      }
      return this.front.conferenceTitleById(registration.conferenceId)
    },
    needsAbstract() {
      const type = this.front.participantForm.reportType
      return type === 'ORAL' || type === 'POSTER'
    },
    currentAbstractName() {
      const registration = this.front.activeParticipantRegistration
      if (!registration) return ''
      if (registration.abstractFileName) return registration.abstractFileName
      if (!registration.abstractFileUrl) return ''
      const raw = String(registration.abstractFileUrl).split('?')[0].split('/').pop()
      try {
        return raw ? decodeURIComponent(raw) : '已上传摘要'
      } catch (e) {
        return raw || '已上传摘要'
      }
    },
    currentAbstractUrl() {
      const registration = this.front.activeParticipantRegistration
      const url = registration && registration.abstractFileUrl
      return url ? this.front.resolveFileUrl(url) : ''
    },
    isStatusWarning() {
      const text = this.front.selfServiceStatus || ''
      return /失败|不能|请|不允许|截止|超过|过期|错误|无权/.test(text) && !/成功|已上传|已保存/.test(text)
    },
    isStatusSuccess() {
      const text = this.front.selfServiceStatus || ''
      return /已保存|已上传|成功/.test(text)
    }
  },
  methods: {
    async onSave() {
      if (this.saving || this.abstractUploading || this.abstractDeleting) return
      this.syncFieldTripToForm()
      this.saving = true
      try {
        await this.front.submitParticipantInfo()
      } finally {
        this.saving = false
        this.$nextTick(() => {
          const el = this.$refs.statusNotice
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
      }
    },
    onFieldTripStageChange() {
      this.selectedRouteIds = []
      this.front.participantForm.fieldTripRoute = ''
      this.front.participantForm.fieldTripSelections = ''
    },
    loadFieldTripSelection() {
      const form = this.front.participantForm
      form.fieldTripStage = this.front.normalizeFieldTripPhase(form.fieldTripStage)
      let ids = []
      const raw = form.fieldTripSelections
      if (raw) {
        try {
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
          if (parsed && typeof parsed === 'object') {
            ids = ['pre', 'during', 'post'].reduce((all, key) => {
              if (Array.isArray(parsed[key])) all.push(...parsed[key].map(String))
              return all
            }, [])
          }
        } catch (e) {
          ids = []
        }
      }
      if (!ids.length && form.fieldTripRoute) {
        ids = [String(form.fieldTripRoute)]
        this.normalizeLegacyRoute(form)
        if (form.fieldTripRoute) ids = [String(form.fieldTripRoute)]
      }
      this.selectedRouteIds = ids
      this.pruneInvalidRoutes()
      this.pruneInvalidAccommodation()
    },
    normalizeLegacyRoute(form) {
      const current = form.fieldTripRoute
      if (!current || !this.front.hasConferenceFieldTripRoutes) return
      const matched = this.front.conferenceFieldTripRoutes.find(
        route => String(route.id) === String(current) || route.name === current
      )
      if (matched) {
        form.fieldTripStage = form.fieldTripStage || this.front.normalizeFieldTripPhase(matched.phase) || matched.phase
        form.fieldTripRoute = matched.id
      }
    },
    pruneInvalidRoutes() {
      const allowed = new Set(this.front.availableFieldTripRoutes.map(route => String(route.id)))
      this.selectedRouteIds = this.selectedRouteIds.filter(id => allowed.has(String(id)))
      if (this.front.fieldTripOnlyPost && this.selectedRouteIds.length > 1) {
        this.selectedRouteIds = this.selectedRouteIds.slice(0, 1)
      }
    },
    pruneInvalidAccommodation() {
      const allowed = this.front.availableAccommodationOptions.map(item => item.value)
      if (!allowed.includes(this.front.participantForm.accommodationType)) {
        this.front.participantForm.accommodationType = 'SELF'
      }
    },
    isRouteSelected(routeId) {
      return this.selectedRouteIds.some(id => String(id) === String(routeId))
    },
    toggleRoute(routeId) {
      if (!this.front.canFillFieldTrip) return
      const key = String(routeId)
      if (this.isRouteSelected(key)) {
        this.selectedRouteIds = this.selectedRouteIds.filter(id => String(id) !== key)
      } else if (this.front.fieldTripOnlyPost) {
        this.selectedRouteIds = [key]
      } else {
        this.selectedRouteIds = this.selectedRouteIds.concat(key)
      }
    },
    syncFieldTripToForm() {
      const form = this.front.participantForm
      if (!this.front.hasConferenceFieldTripRoutes) {
        form.fieldTripStage = this.front.normalizeFieldTripPhase(form.fieldTripStage)
        form.fieldTripSelections = ''
        return
      }
      const ids = (this.selectedRouteIds || []).map(String).filter(Boolean)
      if (!ids.length) {
        form.fieldTripStage = ''
        form.fieldTripRoute = ''
        form.fieldTripSelections = ''
        this.selectedRouteIds = []
        return
      }
      const selections = { pre: [], during: [], post: [] }
      ids.forEach(id => {
        const route = this.front.conferenceFieldTripRoutes.find(item => String(item.id) === id)
        if (route && selections[route.phase]) selections[route.phase].push(id)
      })
      const firstRoute = this.front.conferenceFieldTripRoutes.find(item => String(item.id) === ids[0])
      form.fieldTripStage = firstRoute ? firstRoute.phase : ''
      form.fieldTripRoute = ids[0] || ''
      form.fieldTripSelections = ids.length ? JSON.stringify(selections) : ''
    },
    formatRouteFee(fee) {
      const amount = Number(fee) || 0
      return amount > 0 ? `¥${amount.toFixed(2)}` : '免费'
    },
    triggerAbstractPick() {
      const input = this.$refs.abstractInput
      if (input) input.click()
    },
    openAbstract() {
      if (this.currentAbstractUrl) {
        window.open(this.currentAbstractUrl, '_blank', 'noopener')
      }
    },
    async onAbstractChange(event) {
      this.abstractUploading = true
      try {
        await this.front.uploadAbstractFile(event)
      } finally {
        this.abstractUploading = false
      }
    },
    async onDeleteAbstract() {
      if (this.abstractDeleting || this.abstractUploading) return
      if (!window.confirm('确认删除已上传的摘要？删除后可在截止日前重新上传。')) return
      this.abstractDeleting = true
      try {
        await this.front.deleteAbstractFile()
      } finally {
        this.abstractDeleting = false
      }
    }
  }
}
</script>

<style scoped>
.participant-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  margin-bottom: 8px;
}

.participant-header__nav {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.participant-header h3 {
  margin: 0;
  text-align: center;
  width: 100%;
}

.participant-current-conf {
  margin: 0;
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  color: #4a5568;
}

.participant-current-conf strong {
  color: #002b49;
  font-size: 15px;
}

.abstract-block {
  display: grid;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e5e1da;
  border-radius: 4px;
  background: #fbfaf7;
}

.abstract-block strong {
  color: #002b49;
  font-size: 14px;
}

.abstract-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #2e7d32;
}

.abstract-file__meta {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.abstract-file__link {
  color: #1b6ec2;
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
}

.abstract-file__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.abstract-input-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}

.save-feedback {
  margin-top: 4px;
}

.accommodation-config {
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border: 1px solid #d8d2c8;
  border-left: 4px solid #c5a059;
  border-radius: 4px;
  background: #fbfaf7;
}

.accommodation-config strong {
  color: #002b49;
  font-size: 14px;
}

.accommodation-config p {
  margin: 0;
  color: #334155;
  line-height: 1.7;
  white-space: pre-line;
  word-break: break-word;
}

.accommodation-config small {
  color: #6b7280;
  line-height: 1.5;
}

.field-trip-block {
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e5e1da;
  border-radius: 4px;
  background: #fbfaf7;
}

.field-trip-block__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.field-trip-block__head strong {
  color: #002b49;
  font-size: 14px;
}

.field-trip-count {
  flex-shrink: 0;
  font-size: 12px;
  color: #1f4f7a;
  font-weight: 600;
}

.field-trip-tip {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
}

.field-trip-group + .field-trip-group {
  margin-top: 12px;
}

.field-trip-group h4 {
  margin: 0 0 7px;
  color: #334155;
  font-size: 12px;
}

.field-trip-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}

.field-trip-chip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #d8d2c8;
  border-radius: 4px;
  background: #fff;
  color: #002b49;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.field-trip-chip:hover:not(:disabled) {
  border-color: #1f4f7a;
  background: #f8fbff;
}

.field-trip-chip.active {
  border-color: #002b49;
  background: #eef5fb;
  box-shadow: inset 0 0 0 1px #002b49;
}

.field-trip-chip:disabled,
.field-trip-chip.disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.field-trip-chip__check {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  border: 1px solid #9aa4b2;
  border-radius: 2px;
  background: #fff;
  position: relative;
}

.field-trip-chip.active .field-trip-chip__check {
  border-color: #002b49;
  background: #002b49;
}

.field-trip-chip.active .field-trip-chip__check::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 0;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.field-trip-chip.radio .field-trip-chip__check {
  border-radius: 50%;
}

.field-trip-chip.radio.active .field-trip-chip__check::after {
  left: 3px;
  top: 3px;
  width: 6px;
  height: 6px;
  border: 0;
  border-radius: 50%;
  background: #fff;
  transform: none;
}

.field-trip-chip__body {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.field-trip-chip__name {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  word-break: break-word;
}

.field-trip-chip__fee {
  color: #a65f00;
  font-size: 12px;
  font-weight: 600;
}

.field-trip-chip__tag {
  width: fit-content;
  padding: 1px 6px;
  border: 1px solid #e5e1da;
  border-radius: 2px;
  background: #f3f0ea;
  color: #6b5e4e;
  font-size: 11px;
  line-height: 1.4;
  font-weight: 500;
}

@media (max-width: 640px) {
  .field-trip-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
